/**
 * Duration values passed from the landing page form to Motor Journeys.
 *
 * The landing page form is a plain GET form, so a `duration` query param only
 * reaches Motor Journeys if a control named `duration` is serialised on submit.
 * Motor Journeys parses that param as a single space-separated `"<n> <unit>"`
 * string (see motor-journeys `src/actions/journey/index.ts` →
 * `extractDurationFromParam`) and validates it against
 * `hours <= 12`, `days <= 28`, `weeks <= 4` with a lowercase, pluralisable unit
 * (motor-journeys `src/types/common.ts`). Anything else is silently discarded
 * downstream and the journey falls back to its own default, so every value we
 * emit has to come from this list.
 *
 * IMPORTANT: this module never manufactures a duration. `resolveDurationParam`
 * returns `undefined` for an unset or unrecognised value, and the form then
 * omits the param entirely rather than guessing. What a page sends is a content
 * decision made in Builder, not a default hidden in here — so the value an
 * editor sees on the block is the value customers get.
 *
 * Pages named after a duration all set one. Pages that are not are a mix of ""
 * and "1 day" — an attempt to set them all explicitly was only partly applied,
 * and both values leave the customer on the journey's own 1-day default, so the
 * mix is harmless and was left alone.
 *
 * The catch to remember: a page that passes a duration is a fixed-duration
 * journey downstream, which excludes it from any experiment on the risk-details
 * default. The `tc-default-cover-two-days` scaffolding is still in
 * motor-journeys code (`FeatureFlagsEnum.DefaultCoverTwoDays`,
 * `DefaultCoverTwoDaysExperimentTracker`), so re-enabling it is a LaunchDarkly
 * flip rather than a code change. Before running any such experiment, clear the
 * duration on the pages that should take part.
 */

// Kept as the shared vocabulary for the quick-pick row in LandingPageForm, which
// deliberately offers only these three, while DURATION_OPTIONS below covers
// everything a page can be configured with.
export const FIXED_DURATION_ONE_DAY = '1 day';
export const FIXED_DURATION_TWO_DAYS = '2 days';
export const FIXED_DURATION_ONE_WEEK = '1 week';

const pluralise = (n: number, unit: string) => `${n} ${unit}${n > 1 ? 's' : ''}`;

// Ordered so the Builder enum reads hours → days → weeks. Bounds mirror
// motor-journeys' durationSchema; widening them here would produce values it
// silently rejects.
export const DURATION_OPTIONS: readonly string[] = [
  ...Array.from({ length: 12 }, (_, i) => pluralise(i + 1, 'hour')),
  ...Array.from({ length: 28 }, (_, i) => pluralise(i + 1, 'day')),
  ...Array.from({ length: 4 }, (_, i) => pluralise(i + 1, 'week')),
];

/**
 * Narrows a Builder-configured duration to something Motor Journeys accepts.
 *
 * Returns `undefined` for an unset, empty or unrecognised value so the caller
 * omits the param rather than guessing. This is what satisfies TCEX-3196's
 * unhappy path: an unmappable duration falls back to the journey's current
 * default instead of erroring or sending a bad value.
 */
export const resolveDurationParam = (configured?: string): string | undefined => {
  const trimmed = configured?.trim();
  return trimmed && DURATION_OPTIONS.includes(trimmed) ? trimmed : undefined;
};
